import { useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { AiContentAdvisorFeature, AiContentAdvisorResponse } from 'Models/ArtificialIntelligence';

export interface AiContentAdvisorValues {
    analysisIsLoading: boolean;
    currentContentHasBeenAnalyzed: boolean;
    onAnalyzeButtonClicked: () => void;
    onDismissAnalysis: () => void;

    analysis?: AiContentAdvisorResponse;
}

/**
 * Manages AI Content Advisor state and behavior for a single text field. Feedback persists (even if the content changes) until it is dismissed.
 * `currentContentHasBeenAnalyzed` is `true` when the current content is identical to the content of the last successful request, in which case another request would be redundant. Failed requests are not recorded, so the user can retry them, and dismissing feedback clears the record, so the user can request feedback on the same content again.
 */
export const useAiContentAdvisor = (api: ArtificialIntelligenceApi, content: string, feature: AiContentAdvisorFeature | undefined, onError: (message: string) => void): AiContentAdvisorValues => {
    const [analysis, setAnalysis] = useState<AiContentAdvisorResponse>();
    const [lastAnalyzedContent, setLastAnalyzedContent] = useState<string>();
    const [analysisIsLoading, setAnalysisIsLoading] = useState(false);

    const currentContentHasBeenAnalyzed = content === lastAnalyzedContent;

    const onAnalyzeButtonClicked = async (): Promise<void> => {
        if (analysisIsLoading || currentContentHasBeenAnalyzed || content.trim().length === 0) {
            return;
        }
        try {
            setAnalysisIsLoading(true);
            const response = await api.aiContentAdvisorCreate({ content, feature });
            setAnalysis(response.data);
            setLastAnalyzedContent(content);
        } catch (error) {
            onError(error.message);
        } finally {
            setAnalysisIsLoading(false);
        }
    };

    const onDismissAnalysis = (): void => {
        setAnalysis(undefined);
        setLastAnalyzedContent(undefined);
    };

    return { analysis, analysisIsLoading, currentContentHasBeenAnalyzed, onAnalyzeButtonClicked, onDismissAnalysis };
};
